import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    Collapse,
    IconButton,
    Typography
} from '@material-ui/core';
import ExpandMore from '@material-ui/icons/ExpandMoreOutlined';
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
    root: {
        marginBottom: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:last-child': {
            marginBottom: 0
        }
    },
    selected: {
        borderColor: theme.palette.primary.main,
        boxShadow: theme.shadows[3]
    },
    selectable: {
        cursor: 'pointer'
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 1.5, 0)
    },
    heading: {
        flex: 1,
        minWidth: 0,
        overflowWrap: 'anywhere'
    },
    title: {
        color: theme.palette.primary.main,
        fontWeight: 700,
        overflowWrap: 'anywhere'
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
        marginTop: theme.spacing(.25),
        overflowWrap: 'anywhere'
    },
    content: {
        padding: theme.spacing(1.5),
        '&:last-child': {
            paddingBottom: theme.spacing(1.5)
        }
    },
    field: {
        display: 'flex',
        gap: theme.spacing(1),
        padding: theme.spacing(.65, 0),
        borderTop: `1px solid ${theme.palette.divider}`,
        '&:first-child': {
            borderTop: 0
        }
    },
    /* Linhas do conteúdo expandido: começam em outro container, então a
       divisória do topo precisa ser reafirmada para não colarem na anterior. */
    fieldDetalhe: {
        borderTop: `1px solid ${theme.palette.divider}`
    },
    fieldLabel: {
        flex: '0 0 38%',
        color: theme.palette.text.secondary,
        fontSize: '0.76rem',
        fontWeight: 700,
        overflowWrap: 'anywhere'
    },
    fieldValue: {
        flex: 1,
        minWidth: 0,
        overflowWrap: 'anywhere'
    },
    detailsButton: {
        marginLeft: 'auto',
        padding: theme.spacing(.5)
    },
    expandIcon: {
        transition: theme.transitions.create('transform'),
        '&.expanded': {
            transform: 'rotate(180deg)'
        }
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        borderTop: `1px solid ${theme.palette.divider}`
    }
}));

export default function CardCustomizado(props) {
    const {
        title,
        subtitle,
        fields = [],
        detailFields = [],
        details = null,
        actions = [],
        selected = false,
        selectable = false,
        onSelect,
        defaultExpanded = false,
        className = ''
    } = props;
    const classes = useStyles();
    const [expanded, setExpanded] = React.useState(defaultExpanded);
    const temDetalhes = !!detailFields.length || !!details;

    const renderField = (field, index, detalhe = false) => (
        <div className={clsx(classes.field, detalhe && classes.fieldDetalhe)} key={field.label || index}>
            <Typography component="span" className={classes.fieldLabel}>{field.label}</Typography>
            <Typography component="span" className={classes.fieldValue}>{field.value || '-'}</Typography>
        </div>
    );

    const handleSelect = event => {
        if (selectable && onSelect && !event.target.closest('button')) {
            onSelect();
        }
    };

    return (
        <Card
            component="article"
            className={clsx(classes.root, selected && classes.selected, selectable && classes.selectable, className)}
            onClick={handleSelect}
            role={selectable ? 'radio' : undefined}
            aria-checked={selectable ? selected : undefined}
            tabIndex={selectable ? 0 : undefined}
            onKeyDown={selectable ? event => {
                if ((event.key === 'Enter' || event.key === ' ') && onSelect) {
                    event.preventDefault();
                    onSelect();
                }
            } : undefined}>
            <div className={classes.header}>
                <div className={classes.heading}>
                    <Typography variant="subtitle1" className={classes.title}>{title}</Typography>
                    {subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}
                </div>
                {temDetalhes && (
                    <IconButton
                        type="button"
                        size="small"
                        className={classes.detailsButton}
                        aria-label={expanded ? 'Ocultar detalhes' : 'Exibir detalhes'}
                        onClick={() => setExpanded(previous => !previous)}>
                        <ExpandMore className={clsx(classes.expandIcon, expanded && 'expanded')} />
                    </IconButton>
                )}
            </div>

            <CardContent className={classes.content}>
                {fields.map((field, index) => renderField(field, index))}
                {temDetalhes && (
                    <Collapse in={expanded}>
                        {detailFields.map((field, index) => renderField(field, index, true))}
                        {details}
                    </Collapse>
                )}
            </CardContent>

            {!!actions.length && (
                <CardActions className={classes.actions}>
                    {actions.map((action, index) => (
                        <React.Fragment key={action.label || index}>
                            {action.element || (
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={action.onClick}
                                    disabled={action.disabled}
                                    aria-label={action.label}>
                                    {action.label}
                                </Button>
                            )}
                        </React.Fragment>
                    ))}
                </CardActions>
            )}
        </Card>
    );
}
